import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import config from "services/config";
import { useStore } from "context/store/store";
import { formatCLP } from "utils";
import { Chart } from "chart.js";
import "chart.js/auto";
import * as d3 from "d3";

const CARD = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #e8ecf1",
  overflow: "hidden",
};

const PIE_COLORS = ["#0353a4", "#16a34a", "#d97706", "#7c3aed", "#dc2626", "#0891b2", "#4f46e5"];

const YEAR_BTN = {
  base: {
    padding: "4px 12px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#64748b",
    cursor: "pointer",
    transition: "all 0.15s",
  },
  active: {
    background: "#0353a4",
    color: "#fff",
    border: "1px solid #0353a4",
  },
};

function MonthlySalesChart({ storeData }) {
  const chartRef = useRef(null);
  const canvasRef = useRef(null);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [chartData, setChartData] = useState(null);
  const [loadingYear, setLoadingYear] = useState(false);

  // Use store data for current year, fetch for others
  useEffect(() => {
    if (selectedYear === currentYear) {
      setChartData(storeData);
      return;
    }
    let cancelled = false;
    setLoadingYear(true);
    axios
      .get(`${config.url}/reports/yearly-sales?year=${selectedYear}`)
      .then(({ data }) => {
        if (cancelled) return;
        setChartData(data.map((r) => r.totalSales));
        setLoadingYear(false);
      })
      .catch(() => {
        if (!cancelled) setLoadingYear(false);
      });
    return () => { cancelled = true; };
  }, [selectedYear, currentYear, storeData]);

  useEffect(() => {
    if (!chartData || !canvasRef.current) return;
    if (chartRef.current) chartRef.current.destroy();

    const ctx = canvasRef.current.getContext("2d");
    chartRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
        datasets: [
          {
            label: selectedYear.toString(),
            data: chartData,
            backgroundColor: selectedYear === currentYear ? "rgba(3, 83, 164, 0.8)" : "rgba(100, 116, 139, 0.6)",
            borderRadius: 6,
            borderSkipped: false,
            maxBarThickness: 40,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#0f172a",
            titleFont: { size: 12 },
            bodyFont: { size: 12 },
            padding: 10,
            cornerRadius: 8,
            callbacks: {
              label: (ctx) => formatCLP(ctx.raw),
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: "#94a3b8", font: { size: 11, weight: 500 } },
          },
          y: {
            grid: { color: "#f1f5f9", drawBorder: false },
            ticks: {
              color: "#94a3b8",
              font: { size: 11 },
              callback: (v) => {
                if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
                if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
                return v;
              },
            },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, [chartData, selectedYear, currentYear]);

  return (
    <div style={{ ...CARD, padding: "20px 24px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <p style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", margin: 0 }}>Ventas Mensuales</p>
          <p style={{ fontSize: 12, color: "#94a3b8", margin: 0, marginTop: 2 }}>{selectedYear}</p>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[2024, 2025, 2026].map((y) => (
            <div
              key={y}
              onClick={() => setSelectedYear(y)}
              style={{
                ...YEAR_BTN.base,
                ...(selectedYear === y ? YEAR_BTN.active : {}),
              }}
            >
              {y}
            </div>
          ))}
        </div>
      </div>
      <div style={{ height: 300, position: "relative" }}>
        {loadingYear && (
          <div style={{
            position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(255,255,255,0.7)", zIndex: 2, borderRadius: 8,
          }}>
            <span style={{ fontSize: 13, color: "#64748b" }}>Cargando...</span>
          </div>
        )}
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
}

function SalesDonut({ sales }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!sales || sales.length === 0 || !svgRef.current) return;

    const container = svgRef.current.parentElement;
    const width = Math.min(container.clientWidth, 280);
    const height = 240;
    const radius = Math.min(width, height) / 2 - 10;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);

    const color = d3.scaleOrdinal(PIE_COLORS);
    const arc = d3.arc().innerRadius(radius * 0.55).outerRadius(radius);
    const pie = d3.pie().value((d) => d.totalSales).sort(null);

    svg
      .selectAll("path")
      .data(pie(sales))
      .enter()
      .append("path")
      .attr("d", arc)
      .attr("fill", (_, i) => color(i))
      .attr("stroke", "#fff")
      .attr("stroke-width", 2)
      .style("cursor", "pointer")
      .on("mouseover", function () {
        d3.select(this).transition().duration(150).attr("transform", function (d) {
          const [x, y] = arc.centroid(d);
          return `translate(${x * 0.06},${y * 0.06})`;
        });
      })
      .on("mouseout", function () {
        d3.select(this).transition().duration(150).attr("transform", "translate(0,0)");
      });

    // Center total
    const total = sales.reduce((a, b) => a + b.totalSales, 0);
    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dy", -6)
      .style("font-size", "10px")
      .style("fill", "#94a3b8")
      .style("font-weight", "600")
      .text("TOTAL");
    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dy", 14)
      .style("font-size", "14px")
      .style("fill", "#0f172a")
      .style("font-weight", "700")
      .text(formatCLP(total));
  }, [sales]);

  const total = sales ? sales.reduce((a, b) => a + b.totalSales, 0) : 0;

  return (
    <div style={{ ...CARD, padding: "20px 24px" }}>
      <p style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", margin: 0, marginBottom: 16 }}>
        Ventas por vendedor
      </p>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <svg ref={svgRef} style={{ overflow: "visible" }} />
      </div>
      {sales && sales.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {sales.map((s, i) => {
            const pct = total > 0 ? ((s.totalSales / total) * 100).toFixed(1) : 0;
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "6px 0",
                  borderBottom: i < sales.length - 1 ? "1px solid #f1f5f9" : "none",
                }}
              >
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: PIE_COLORS[i % PIE_COLORS.length],
                    marginRight: 10,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 12, color: "#475569", flex: 1 }}>{s.name}</span>
                <span style={{ fontSize: 11, color: "#94a3b8", marginRight: 10 }}>{pct}%</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>
                  {formatCLP(s.totalSales)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TopProductsTable({ products }) {
  return (
    <div style={{ ...CARD }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #e8ecf1" }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", margin: 0 }}>
          Productos mas vendidos
        </p>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              <th style={TH_STYLE}>Producto</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>Monto</th>
            </tr>
          </thead>
          <tbody>
            {products &&
              products.length > 0 &&
              products.map((p, i) => (
                <tr
                  key={i}
                  style={{
                    borderBottom: i < products.length - 1 ? "1px solid #f1f5f9" : "none",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td style={{ padding: "12px 20px", fontSize: 13, color: "#334155" }}>{p.name}</td>
                  <td
                    style={{
                      padding: "12px 20px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#0f172a",
                      textAlign: "right",
                    }}
                  >
                    {formatCLP(p.totalPrice)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TH_STYLE = {
  padding: "10px 20px",
  fontSize: 11,
  fontWeight: 600,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  textAlign: "left",
};

const SUMMARY_COLORS = [
  { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
  { bg: "#faf5ff", color: "#7c3aed", border: "#ddd6fe" },
  { bg: "#fff7ed", color: "#d97706", border: "#fed7aa" },
  { bg: "#f0fdfa", color: "#0d9488", border: "#99f6e4" },
];

function MiniStat({ label, value, idx = 0 }) {
  const c = SUMMARY_COLORS[idx % SUMMARY_COLORS.length];
  return (
    <div
      style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: 10,
        padding: "14px 16px",
        textAlign: "center",
      }}
    >
      <p style={{ fontSize: 22, fontWeight: 700, color: c.color, margin: 0, lineHeight: 1.2 }}>
        {value ?? "--"}
      </p>
      <p style={{ fontSize: 11, fontWeight: 500, color: "#64748b", margin: 0, marginTop: 4 }}>
        {label}
      </p>
    </div>
  );
}

function MemberSummary({ data }) {
  if (!data) return null;
  const { members, byOwner } = data;

  return (
    <div style={{ ...CARD, padding: "20px 24px" }}>
      <p style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", margin: 0, marginBottom: 16 }}>
        Resumen de Clientes
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
          gap: 10,
          marginBottom: 20,
        }}
      >
        <MiniStat label="Total Clientes" value={members.total} idx={0} />
        <MiniStat label="Activos" value={members.active} idx={1} />
        <MiniStat label="Inactivos" value={members.inactive} idx={2} />
        <MiniStat label="Con Drive" value={members.withDrive} idx={3} />
        <MiniStat label="Con Liquidaciones" value={members.withLiquidaciones} idx={4} />
        <MiniStat label="Total Trabajadores" value={members.totalWorkers} idx={5} />
      </div>

      {byOwner && byOwner.length > 0 && (
        <>
          <p
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#475569",
              margin: 0,
              marginBottom: 8,
              paddingTop: 12,
              borderTop: "1px solid #e8ecf1",
            }}
          >
            Por Ejecutivo
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={TH_STYLE}>Ejecutivo</th>
                <th style={{ ...TH_STYLE, textAlign: "center" }}>Clientes</th>
                <th style={{ ...TH_STYLE, textAlign: "center" }}>Activos</th>
                <th style={{ ...TH_STYLE, textAlign: "center" }}>Inactivos</th>
              </tr>
            </thead>
            <tbody>
              {byOwner.map((o, i) => (
                <tr
                  key={o._id}
                  style={{
                    borderBottom: i < byOwner.length - 1 ? "1px solid #f1f5f9" : "none",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td style={{ padding: "10px 20px", fontSize: 13, color: "#334155", fontWeight: 500 }}>
                    {o.name}
                  </td>
                  <td style={{ padding: "10px 20px", fontSize: 13, color: "#0f172a", fontWeight: 600, textAlign: "center" }}>
                    {o.count}
                  </td>
                  <td style={{ padding: "10px 20px", fontSize: 13, color: "#16a34a", fontWeight: 600, textAlign: "center" }}>
                    {o.active}
                  </td>
                  <td style={{ padding: "10px 20px", fontSize: 13, color: "#dc2626", fontWeight: 600, textAlign: "center" }}>
                    {o.count - o.active}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function TopWorkersTable({ workers }) {
  if (!workers || workers.length === 0) return null;
  return (
    <div style={{ ...CARD }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #e8ecf1" }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", margin: 0 }}>
          Top 10 Clientes por Trabajadores
        </p>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              <th style={{ ...TH_STYLE, width: 30 }}>#</th>
              <th style={TH_STYLE}>Cliente</th>
              <th style={TH_STYLE}>Empresa</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>Trabajadores</th>
            </tr>
          </thead>
          <tbody>
            {workers.map((w, i) => (
              <tr
                key={w._id}
                style={{
                  borderBottom: i < workers.length - 1 ? "1px solid #f1f5f9" : "none",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <td style={{ padding: "10px 20px", fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>
                  {i + 1}
                </td>
                <td style={{ padding: "10px 20px", fontSize: 13, color: "#334155", fontWeight: 500 }}>
                  {w.name}
                </td>
                <td style={{ padding: "10px 20px", fontSize: 12, color: "#64748b" }}>
                  {w.company_information?.name || "-"}
                </td>
                <td
                  style={{
                    padding: "10px 20px",
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#0353a4",
                    textAlign: "right",
                  }}
                >
                  {w.workersTotal}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const store = useStore();
  const summary = store.reports.dashboardSummary;

  return (
    <div style={{ padding: "0 32px 32px" }}>
      {/* Row 1: Chart + Pie */}
      <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 16, marginBottom: 16 }}>
        <MonthlySalesChart storeData={store.reports.yearlySales} />
        <SalesDonut sales={store.reports.salesBySalesman} />
      </div>

      {/* Row 2: Member Summary + Top Products */}
      <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 16, marginBottom: 16 }}>
        <MemberSummary data={summary} />
        <TopProductsTable products={store.reports.topSellingProducts} />
      </div>

      {/* Row 3: Top Workers */}
      <TopWorkersTable workers={summary?.topWorkers} />
    </div>
  );
}
